import { Check, Info } from "lucide-react";
import { usePageMeta } from "@/lib/seo";
import { IMAGES, PARTNER_TYPES, PARTNER_BENEFITS, PARTNER_INTERESTS } from "@/content/site";
import { PageHero } from "@/components/site/PageHero";
import { Reveal, SectionHeader, CtaLink, IconTile } from "@/components/site/primitives";
import { EnquiryForm } from "@/components/site/EnquiryForm";

export default function PartnersPage() {
  usePageMeta({
    title: "For Healthcare Partners — Doctors, Labs, Pharmacies & NGOs",
    description: "Partner with SwastSaathi: licensed doctors, diagnostic laboratories, pharmacies, hospitals, NGOs and institutions interested in rural primary healthcare in Bihar.",
    path: "/partners",
  });
  return (
    <>
      <PageHero
        testId="partners-hero"
        eyebrow="For healthcare partners"
        title="Reach the communities that need you most — together."
        intro="SwastSaathi is seeking conversations with licensed providers and community organisations who want to help validate a partnership-led model for rural primary healthcare in Bihar."
        image={IMAGES.telePartners}
        imageAlt="An Indian doctor smiling during a video teleconsultation with a rural patient and a community health worker"
      >
        <CtaLink to="#enquiry" testId="partners-hero-enquiry-cta">Start a partnership enquiry</CtaLink>
      </PageHero>

      <section data-testid="partners-types" className="bg-white py-20 lg:py-28">
        <div className="container-x">
          <SectionHeader eyebrow="Who we hope to work with" title="Six kinds of partners. One shared goal." />
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {PARTNER_TYPES.map((p, i) => (
              <Reveal key={p.title} delay={(i % 3) * 0.07}>
                <article data-testid={`partner-type-${i}`} className="card-soft card-soft-hover h-full p-7">
                  <IconTile icon={p.icon} tone={i % 2 ? "plum" : "teal"} />
                  <h3 className="mt-6 text-xl font-semibold">{p.title}</h3>
                  <p className="mt-2 leading-relaxed text-slate">{p.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="partners-benefits" className="bg-mint py-20 lg:py-28">
        <div className="container-x grid gap-14 lg:grid-cols-12">
          <SectionHeader className="lg:col-span-5" eyebrow="Potential benefits" title="Why collaborate with SwastSaathi?" intro="Benefits we expect to offer partners as the model is piloted. Specific terms would be agreed individually and in writing." />
          <ul className="space-y-3 lg:col-span-7">
            {PARTNER_BENEFITS.map((b, i) => (
              <Reveal as="li" key={b} delay={i * 0.05}>
                <div className="flex gap-4 rounded-2xl bg-white p-5">
                  <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-teal text-white"><Check className="h-4 w-4" strokeWidth={2.5} /></span>
                  <p className="text-[16px] leading-relaxed text-ink">{b}</p>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section id="enquiry" data-testid="partners-enquiry" className="scroll-mt-24 bg-white py-20 lg:py-28">
        <div className="container-x grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHeader eyebrow="Partnership enquiry" title="Tell us how you'd like to collaborate." />
            <Reveal delay={0.1} className="mt-8 rounded-2xl border border-plum/10 bg-plum-50 p-6">
              <p className="flex gap-3 text-sm leading-relaxed text-plum">
                <Info className="mt-0.5 h-5 w-5 shrink-0" />
                <span><strong>Please note:</strong> submitting this form is an enquiry only. It does not establish a formal partnership, agreement or commitment by either party.</span>
              </p>
            </Reveal>
          </div>
          <Reveal className="card-soft p-6 sm:p-10 lg:col-span-8">
            <EnquiryForm mode="partnership" idPrefix="partner" interests={PARTNER_INTERESTS} interestLabel="Partnership interest" />
          </Reveal>
        </div>
      </section>
    </>
  );
}
